// src/live/utils/coerceTimestamp.ts
// Shared timestamp coercion — single source of truth for the live tab.

/**
 * Coerce an unknown timestamp-like value into milliseconds since epoch.
 *
 * Handles:
 *  - Firestore Timestamp (has `.toMillis()` or `.toDate()`)
 *  - Date objects
 *  - ISO 8601 strings
 *  - Numeric milliseconds
 *  - Plain objects with `seconds` / `nanoseconds` fields
 *
 * Returns 0 for null, undefined, or unparseable values.
 */
export const coerceTimestamp = (value: unknown): number => {
  if (value == null) return 0;

  // Firestore Timestamp — has .toMillis()
  if (typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }

  // Date object
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  // Numeric milliseconds
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  // ISO string or other parseable date string
  if (typeof value === 'string') {
    const parsed = new Date(value);
    const ms = parsed.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  // Plain object with seconds / nanoseconds (serialized Firestore Timestamp)
  if (typeof value === 'object') {
    const maybeTs = value as {
      seconds?: unknown;
      nanoseconds?: unknown;
      toDate?: () => Date;
    };

    if (
      typeof maybeTs.seconds === 'number' &&
      typeof maybeTs.nanoseconds === 'number'
    ) {
      return maybeTs.seconds * 1000 + Math.floor(maybeTs.nanoseconds / 1_000_000);
    }

    if (typeof maybeTs.toDate === 'function') {
      try {
        const converted = maybeTs.toDate();
        if (converted instanceof Date && !Number.isNaN(converted.getTime())) {
          return converted.getTime();
        }
      } catch {
        // Ignore malformed timestamp-like objects and fall through to 0.
      }
    }
  }

  return 0;
};

/**
 * Convenience wrapper: coerce an unknown value to a `Date`.
 * Returns `new Date(0)` for null / undefined / unparseable values.
 */
export const coerceTimestampToDate = (value: unknown): Date =>
  new Date(coerceTimestamp(value));
